import { RefObject, useEffect } from 'react';

const WARNING_CLASS = 'warning';
const OFFSET_PROPERTY = '--fixed-warning-bottom';

const findSiblingWarning = (element: HTMLElement) =>
  Array.from(element.parentElement?.children ?? []).find(sibling => sibling.classList.contains(WARNING_CLASS)) as HTMLElement | undefined;

// O Warning do portal é fixo abaixo do cabeçalho e a altura dele depende do texto do alerta. Como ele sai do
// fluxo, a faixa mede onde ele termina e expõe esse ponto numa variável CSS, para descer logo abaixo dele.
// A medida usa a caixa de layout (offsetTop + offsetHeight): o getBoundingClientRect pegaria o scaleY da animação de
// entrada do rsuite, e o fim da animação não dispara nenhum observer para corrigir.
export const useFixedWarningOffset = (layoutRef: RefObject<HTMLElement>, enabled: boolean) => {
  useEffect(() => {
    const layout = layoutRef.current;
    const container = layout?.parentElement;
    if (!enabled || !layout || !container) return undefined;

    let observedWarning: HTMLElement | undefined;
    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => updateOffset());

    function updateOffset() {
      const warning = findSiblingWarning(layout as HTMLElement);
      if (warning !== observedWarning) {
        if (observedWarning) resizeObserver?.unobserve(observedWarning);
        if (warning) resizeObserver?.observe(warning);
        observedWarning = warning;
      }

      if (!warning) {
        layout?.style.removeProperty(OFFSET_PROPERTY);
        return;
      }
      layout?.style.setProperty(OFFSET_PROPERTY, `${warning.offsetTop + warning.offsetHeight}px`);
    }

    const mutationObserver = new MutationObserver(updateOffset);
    mutationObserver.observe(container, { childList: true });
    updateOffset();

    return () => {
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
      layout.style.removeProperty(OFFSET_PROPERTY);
    };
  }, [layoutRef, enabled]);
};
